import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { useAuthentication } from 'src/features/authentication/context';
import { ReportsService } from 'src/services/reports/reports.service';
import type { TemplateImageKind } from 'src/services/reports/types';

import { getImageContentType } from '../utils/imageContentType';

const FALLBACK_ERROR_MESSAGE = 'Image upload failed. Please try again.';

type UploadTemplateImageArgs = { kind: TemplateImageKind; file: File };

// The server's 422 copy (`detail`) when it gives one, so a rejected image says why.
export const getUploadErrorMessage = (error: unknown) => {
  if (
    axios.isAxiosError(error) &&
    error.response?.status === 422 &&
    typeof error.response.data?.detail === 'string'
  ) {
    return error.response.data.detail;
  }
  return FALLBACK_ERROR_MESSAGE;
};

// Shared by both kinds: read the result from mutateAsync, not the mutation's own state.
export const useUploadTemplateImageMutation = () => {
  const {
    customerId: { value: customerId },
  } = useAuthentication();

  return useMutation({
    mutationFn: async ({ kind, file }: UploadTemplateImageArgs) => {
      if (!customerId) throw new Error('No customer selected');
      const contentType = getImageContentType(file);
      if (!contentType) throw new Error(`Unsupported image type ${file.type || file.name}`);
      return ReportsService.uploadTemplateImage(customerId, kind, file, contentType);
    },
  });
};
